import { Modal, Select, Tooltip } from '@byecode/ui'
import type { UnionAssociateNodes } from '@lighthouse/shared'
import { type AggregatorNode, type AggregatorUnionConfig as UnionConfig } from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import * as SC from '../styles'
import { SelectField } from './SelectField'
import { UnionMap } from './UnionMap'

interface AggregatorUnionConfigProps {
    nodeList: AggregatorNode[]
}

export const AggregatorUnionConfig: React.FC<AggregatorUnionConfigProps> = ({ nodeList }) => {
    const { control, reset, getValues } = useFormContext<UnionConfig>()
    const primaryDsNodeId = useWatch({
        control,
        name: 'primaryDsNodeId'
    })

    const { primaryDsNode, unionDsNode } = useMemo(
        () =>
            nodeList.reduce<UnionAssociateNodes>(
                (prev, cur) => {
                    if (cur.id === primaryDsNodeId) {
                        prev.primaryDsNode = cur
                        return prev
                    }
                    prev.unionDsNode = cur
                    return prev
                },
                {
                    primaryDsNode: undefined,
                    unionDsNode: undefined
                }
            ),
        [nodeList, primaryDsNodeId]
    )

    const nodeOptions = useMemo(() => {
        return nodeList.map(item => ({
            label: item.data.config.name,
            value: item.id
        }))
    }, [nodeList])

    const handleChangePrimaryDsNodeId = useCallback(
        (val: string) => {
            const [firstNode, secondNode] = nodeList
            if (firstNode.id === val) {
                const primaryFieldList = firstNode?.data.result?.fieldList || []
                const linkFieldList = secondNode?.data.result?.fieldList || []
                reset({
                    name: getValues('name'),
                    primaryDsNodeId: val,
                    unionFields: primaryFieldList.map(item => ({
                        primaryFieldId: item.id,
                        unionFieldId: linkFieldList.find(link => link.name === item.name && link.innerType === item.innerType)?.id || ''
                    }))
                })
                return
            }
            const primaryFieldList = secondNode?.data.result?.fieldList || []
            const linkFieldList = firstNode?.data.result?.fieldList || []
            reset({
                name: getValues('name'),
                primaryDsNodeId: val,
                unionFields: primaryFieldList.map(item => ({
                    primaryFieldId: item.id,
                    unionFieldId: linkFieldList.find(link => link.name === item.name && link.innerType === item.innerType)?.id || ''
                }))
            })
        },
        [getValues, nodeList, reset]
    )

    const handleSelect = useCallback(
        async (val: string) => {
            if (!primaryDsNodeId) {
                handleChangePrimaryDsNodeId(val)
                return
            }
            const confirm = await Modal.confirm({
                title: '确定切换主表？',
                content: '切换后，将会初始化所有字段匹配的配置，您先前的操作不会保留。',
                okText: '确定'
            })
            if (confirm) {
                handleChangePrimaryDsNodeId(val)
            }
        },
        [handleChangePrimaryDsNodeId, primaryDsNodeId]
    )

    return (
        <SC.FormContainer>
            <SC.FormItem>
                <SC.FormItemLabelWrapper>
                    <SC.FormItemLabel>主表</SC.FormItemLabel>
                    <Tooltip title="合并后的字段类型与主表同步">
                        <SC.Icon size={16} type="Question" />
                    </Tooltip>
                </SC.FormItemLabelWrapper>
                <SC.FormItemContent>
                    <Controller
                        name="primaryDsNodeId"
                        control={control}
                        render={({ field }) => (
                            <Select style={{ width: 274 }} options={nodeOptions} value={field.value} onChange={handleSelect} />
                        )}
                    />
                </SC.FormItemContent>
            </SC.FormItem>
            {primaryDsNode && unionDsNode && (
                <Controller
                    name="unionFields"
                    control={control}
                    render={({ field }) => (
                        <>
                            <SC.FormItem>
                                <SC.FormItemLabelWrapper>
                                    <SC.FormItemLabel>字段匹配</SC.FormItemLabel>
                                </SC.FormItemLabelWrapper>
                                <SC.FormItemContent>
                                    <UnionMap
                                        primaryDsNode={primaryDsNode}
                                        unionDsNode={unionDsNode}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                    <SelectField
                                        fieldList={primaryDsNode?.data.result?.fieldList || []}
                                        value={field.value?.map(item => item.primaryFieldId)}
                                        onSelect={val => {
                                            field.onChange([
                                                ...(field?.value || []),
                                                {
                                                    primaryFieldId: val,
                                                    unionFieldId: ''
                                                }
                                            ])
                                        }}
                                    />
                                </SC.FormItemContent>
                            </SC.FormItem>
                        </>
                    )}
                />
            )}
        </SC.FormContainer>
    )
}
